import { useCallback, useMemo } from "react";

import { Accordion } from "@/components/accordions/accordion";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { cx } from "@/utils/cx";

import type { LocationManagementState } from "../../../hooks/useLocationManagementState";

import {
	getLocationMapFilteredAccordionOpenPanels,
	type LocationMapZoneSearchEntry,
	mapFilteredAccordionOpenPanelsToOriginal,
} from "../../../utils/location/location-map-utils";
import { SensorRow } from "./sensor-row";
import { ZoneAccordionTitle } from "./zone-accordion-title";

type ZoneAccordionListProps = {
	entries: LocationMapZoneSearchEntry[];
	isSidebarOpen?: boolean;
	mapState: LocationManagementState;
	/** Zones rendered under a floor sit further in than top-level zones. */
	nested?: boolean;
	onAddSensor?: (zoneId: string) => void;
	onAddZone?: () => void;
	searchQuery?: string;
};

/** Zone accordions for one floor, or for zones that are not on a floor. */
export const ZoneAccordionList = ({
	entries,
	isSidebarOpen = true,
	mapState,
	nested = false,
	onAddSensor,
	onAddZone,
	searchQuery = "",
}: ZoneAccordionListProps) => {
	const {
		activeFeature,
		canEditSensorDetails,
		clearHoveredSensor,
		editDraft,
		editingSensorId,
		editingZoneId,
		focusSensor,
		handleOpenPanelsChange,
		hoveredSensorId,
		hoverSensor,
		isFloorEditActive,
		openPanels,
		startSensorEdit,
		toggleZonePanel,
	} = mapState;

	const accordionOpenPanels = useMemo(
		() =>
			getLocationMapFilteredAccordionOpenPanels(
				entries,
				openPanels,
				searchQuery,
			),
		[entries, openPanels, searchQuery],
	);

	const handleFilteredOpenPanelsChange = useCallback(
		(nextFiltered: Set<number>) => {
			handleOpenPanelsChange(
				mapFilteredAccordionOpenPanelsToOriginal(
					nextFiltered,
					entries,
					openPanels,
				),
			);
		},
		[entries, handleOpenPanelsChange, openPanels],
	);

	const accordionItems = useMemo(
		() =>
			entries.map((entry, filteredIndex) => {
				const { sensors: zoneSensors, zone, zoneIndex } = entry;
				const isTargetZone = !!editingSensorId && editDraft?.zoneId === zone.id;
				const isZoneOpen = accordionOpenPanels.has(filteredIndex);
				const isZoneSelected = isZoneOpen || editingZoneId === zone.id;

				return {
					content: (
						<div className="bg-secondary p-5">
							<div className="rounded-xl shadow-sm">
								<div className="overflow-hidden rounded-xl bg-primary">
									<p className="border-b border-secondary p-4 text-sm font-medium text-primary">
										Sensors
									</p>
									{zoneSensors.length === 0 ? (
										<div className="flex items-center justify-between gap-3 px-4 py-3">
											<p className="text-sm text-tertiary">
												No sensors in this zone.
											</p>
											{onAddSensor ? (
												<Button
													color="link-color"
													onClick={() => onAddSensor(zone.id)}
													size="sm"
												>
													Add Sensor
												</Button>
											) : null}
										</div>
									) : (
										<ul className="divide-y divide-secondary bg-primary">
											{zoneSensors.map((sensor) => {
												const isActive =
													activeFeature?.id === sensor.id ||
													editingSensorId === sensor.id;
												const isHovered =
													!isActive && hoveredSensorId === sensor.id;
												const isBeingEdited = editingSensorId === sensor.id;

												return (
													<li key={sensor.id}>
														<SensorRow
															allowScrollIntoView={isSidebarOpen}
															canEditSensorDetails={canEditSensorDetails}
															editingLabel={
																isBeingEdited ? "Editing above" : undefined
															}
															isActive={isActive}
															isEditDisabled={
																!!editingSensorId ||
																!!editingZoneId ||
																isFloorEditActive
															}
															isHovered={isHovered}
															onAddSensor={
																onAddSensor
																	? () => onAddSensor(zone.id)
																	: undefined
															}
															onEdit={() => startSensorEdit(sensor, zone.id)}
															onFocus={() => focusSensor(sensor.id, zone.id)}
															onHoverEnd={clearHoveredSensor}
															onHoverStart={() => hoverSensor(sensor.id)}
															sensor={sensor}
														/>
													</li>
												);
											})}
										</ul>
									)}
								</div>
							</div>
						</div>
					),
					headerClassName: cx(
						"border-b-1 border-l-2 bg-primary py-2 transition-colors",
						nested ? "pr-2 pl-4" : "px-4",
						isZoneSelected
							? "border-b-secondary border-l-brand-500"
							: "border-transparent",
					),
					id: zone.id,
					title: (
						<ZoneAccordionTitle
							isActive={isZoneSelected}
							isExpanded={isZoneOpen}
							mapState={mapState}
							onAddSensor={onAddSensor ? () => onAddSensor(zone.id) : undefined}
							onAddZone={onAddZone}
							onToggle={() => toggleZonePanel(zoneIndex)}
							sensorCount={zoneSensors.length}
							titleClassName={cx(isTargetZone && "text-brand")}
							trailing={
								isTargetZone ? (
									<Badge color="brand" size="sm" type="pill-color">
										Target zone
									</Badge>
								) : null
							}
							zone={zone}
						/>
					),
				};
			}),
		[
			accordionOpenPanels,
			activeFeature?.id,
			canEditSensorDetails,
			clearHoveredSensor,
			editDraft?.zoneId,
			editingSensorId,
			editingZoneId,
			entries,
			focusSensor,
			hoveredSensorId,
			hoverSensor,
			isFloorEditActive,
			isSidebarOpen,
			mapState,
			nested,
			onAddSensor,
			onAddZone,
			startSensorEdit,
			toggleZonePanel,
		],
	);

	if (accordionItems.length === 0) return null;

	return (
		<Accordion
			buttonClassName="py-6"
			className="gap-0"
			items={accordionItems}
			onOpenPanelsChange={handleFilteredOpenPanelsChange}
			openPanels={accordionOpenPanels}
			scrollOpenedPanelIntoView
			transition={{ duration: 0.25, ease: "easeOut", type: "tween" }}
		/>
	);
};
